import { FileUpload } from '@/components/documents/FileUpload';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useTaxYear } from '@/contexts/TaxYearContext';
import { getFullName } from '@/lib/utils';
import { uploadDocuments } from '@/services/documentApi';
import { UploadProgress } from '@/types/document';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface FileWithId extends File {
  id: string;
}

interface UploadProgressState {
  id: string;
  file: FileWithId;
  progress: number;
  status: UploadProgress['status'];
  error?: string;
}

export default function UploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedYear } = useTaxYear();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState[]>([]);

  const handleFilesSelected = () => {
    // Reset progress when files change
    setUploadProgress([]);
  };

  const handleUpload = async (files: File[]) => {
    if (!user) return;
    
    setIsUploading(true);
    const filesWithId = files as FileWithId[];
    
    // Initialize progress for all files
    setUploadProgress(
      filesWithId.map((file) => ({
        id: file.id,
        file,
        progress: 0,
        status: 'pending' as const,
      }))
    );

    try {
      const userName = getFullName(user);
      
      await uploadDocuments(
        user.id,
        userName,
        files,
        (fileIndex, progress, status) => {
          setUploadProgress((prev) => {
            const newProgress = [...prev];
            if (newProgress[fileIndex]) {
              newProgress[fileIndex] = {
                ...newProgress[fileIndex],
                progress,
                status,
              };
            }
            return newProgress;
          });
        },
        selectedYear
      );

      toast.success(`Successfully uploaded ${files.length} document${files.length !== 1 ? 's' : ''}`);
      
      // Navigate to documents page after short delay
      setTimeout(() => {
        navigate('/documents');
      }, 1500);
    } catch (error) {
      toast.error('Failed to upload some documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4 min-w-0">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Go back</span>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Upload Documents</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-0.5">
            Upload your tax documents for the selected year. You can upload 1 to 60 files at a time.
          </p>
        </div>
      </div>

      {/* Upload Card */}
      <Card className="min-w-0 overflow-hidden">
        <CardContent className="space-y-6 pt-6 px-4 sm:px-6">
          <FileUpload
            onFilesSelected={handleFilesSelected}
            onUpload={handleUpload}
            maxFiles={60}
            minFiles={1}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
            <li>Supported formats: PDF, JPG, PNG, Excel (XLS, XLSX)</li>
            <li>Maximum file size: 10MB per file</li>
            <li>You can upload between 1 and 60 files at once</li>
            <li>Ensure documents are clear and readable</li>
            <li>Personal information will be kept secure and encrypted</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
